"use client"

import type React from "react"
import { useEffect, useState } from "react"

import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";

import CodeIcon from "@mui/icons-material/Code";
import PreviewIcon from "@mui/icons-material/Visibility";

import {
  MDXEditor,
  UndoRedo,
  ListsToggle,
  DiffSourceToggleWrapper,
  BlockTypeSelect,
  BoldItalicUnderlineToggles,
} from "@mdxeditor/editor"

import {
  headingsPlugin,
  listsPlugin,
  toolbarPlugin,
  quotePlugin,
  thematicBreakPlugin,
  markdownShortcutPlugin,
  diffSourcePlugin,
} from "@mdxeditor/editor"
import "@mdxeditor/editor/style.css"
import { Worker, Viewer } from "@react-pdf-viewer/core"
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout"
import "@react-pdf-viewer/core/lib/styles/index.css"
import "@react-pdf-viewer/default-layout/lib/styles/index.css"

interface MarkdownEditorProps {
  content: string
  onChange?: (content: string) => void
  initialContent?: string
  filePath?: string
}

const MarkdownEditor: React.FC<MarkdownEditorProps> = ({ content, initialContent, filePath, onChange }) => {
  const [activeTab, setActiveTab] = useState(0)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const defaultLayoutPluginInstance = defaultLayoutPlugin()

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  // PNG 이미지(보호 컨테이너) 미리보기용 fetch → objectURL 생성
  useEffect(() => {
    if (!filePath || !filePath.toLowerCase().endsWith('.png')) {
      setImageUrl(null)
      return
    }

    let objectUrl: string | null = null
    const controller = new AbortController()

    const fetchImage = async () => {
      try {
        const token = sessionStorage.getItem('storageAccessToken')
        const res = await fetch(filePath, {
          method: 'GET',
          headers: {
            // 'Authorization': token ? `Bearer ${token}` : '',
            'x-ms-version': '2020-04-08',
            'x-ms-date': new Date().toUTCString(),
            'referer': 'https://ari.gsrdt.com',
          },
          signal: controller.signal,
        })
        if (!res.ok) {
          throw new Error(`Failed to load image: ${res.status}`)
        }
        const blob = await res.blob()
        objectUrl = URL.createObjectURL(blob)
        setImageUrl(objectUrl)
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e)
        if (objectUrl) URL.revokeObjectURL(objectUrl)
        setImageUrl(null)
      }
    }

    fetchImage()

    return () => {
      controller.abort()
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [filePath])
  return (
    <Box sx={{ height: "calc(100dvh - 140px)", display: "flex", flexDirection: "column" }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ minHeight: 48 }} >
          <Tab icon={<CodeIcon />} iconPosition="start"  label="Markdown" sx={{ minHeight: 48 }} />
          <Tab icon={<PreviewIcon />} iconPosition="start" label="미리보기" sx={{ minHeight: 48 }} />
        </Tabs>
      </Box>

      <Box sx={{ height: "100%", flex: 1, overflow: "auto", overscrollBehavior: "contain", overflowAnchor: "none" }}>
        <Box sx={{ height: "100%", display: activeTab === 0 ? 'block' : 'none' }}>
          <MDXEditor
            markdown={content}
            onChange={(value) => onChange?.(value)}
            plugins={[
              headingsPlugin(),
              listsPlugin(),
              toolbarPlugin({
                toolbarClassName: "dm-toolbar",
                toolbarContents: () => (
                  <DiffSourceToggleWrapper>
                    <UndoRedo />
                    <BoldItalicUnderlineToggles />
                    <BlockTypeSelect />
                    <ListsToggle />
                  </DiffSourceToggleWrapper>
                ),
              }),
              diffSourcePlugin({
                diffMarkdown: initialContent ?? content,
                viewMode: "rich-text",
                readOnlyDiff: true,
              }),
              quotePlugin(),
              thematicBreakPlugin(),
              markdownShortcutPlugin(),
            ]}
          />
        </Box>

        <Box sx={{ height: "100%", overflow: "auto", p: 1, display: activeTab === 1 ? 'block' : 'none' }}>
          {!filePath && (
            <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: 'text.secondary' }}>
              미리보기 데이터가 없습니다.
            </Box>
          )}

          {filePath && filePath.toLowerCase().endsWith('.png') && imageUrl && (
            <Box sx={{ height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
              <img src={imageUrl} alt="Preview" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
            </Box>
          )}

          {filePath && !filePath.toLowerCase().endsWith('.png') && (
            <Worker workerUrl={`https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js`}>
              <Box sx={{ height: "v" }}>
                <Viewer
                  key={filePath}
                  plugins={[defaultLayoutPluginInstance]}
                  fileUrl={filePath}
                  httpHeaders={{
                    // 'Authorization': `Bearer ${sessionStorage.getItem("storageAccessToken")}`,
                    'Content-Type': 'application/pdf',
                    'x-ms-version': '2020-04-08',
                    'x-ms-date': new Date().toUTCString(),
                  }}
                />
              </Box>
            </Worker>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default MarkdownEditor
